import { createContext, useContext, useState } from 'react';
import { useAui, useAuiState } from '@assistant-ui/react';
import PropTypes from 'prop-types';
import { Collapse, Flex, Spin, Typography } from 'antd';
import { MarkdownText } from '@/common/components/MarkdownText';
import { getReasoningPartIndex, getReasoningState } from '../utils/reasoning-state';

export const AgentReasoningFinishContext = createContext(new Map());

export function AgentReasoningContent({ status, text = '', finishOutcome }) {
  const [userExpanded, setUserExpanded] = useState();
  const { expanded, isRunning, title } = getReasoningState(status, userExpanded, finishOutcome);
  const label = (
    <Flex align="center" gap="small">
      {isRunning ? <Spin size="small" /> : null}
      <Typography.Text type="secondary">{title}</Typography.Text>
    </Flex>
  );

  if (!text.trim()) {
    if (finishOutcome === 'complete') return null;
    return isRunning || finishOutcome || status?.type === 'incomplete' ? label : null;
  }

  return (
    <Collapse
      ghost
      size="small"
      activeKey={expanded ? ['reasoning'] : []}
      onChange={(keys) => setUserExpanded(keys.includes('reasoning'))}
      items={[{ key: 'reasoning', label, children: <MarkdownText /> }]}
    />
  );
}

AgentReasoningContent.propTypes = {
  finishOutcome: PropTypes.oneOf(['complete', 'cancelled', 'error']),
  status: PropTypes.shape({
    reason: PropTypes.string,
    type: PropTypes.string,
  }),
  text: PropTypes.string,
};

export default function AgentReasoningMessage(props) {
  const partIndex = useAui().part.query.index;
  const messageId = useAuiState((state) => state.message.id);
  const reasoningIndex = useAuiState((state) =>
    getReasoningPartIndex(state.message.parts, partIndex),
  );
  const outcomes = useContext(AgentReasoningFinishContext);

  // assistant-ui reuses message/part slots by index, not by message identity.
  return (
    <AgentReasoningContent
      key={`${messageId}:${partIndex}`}
      {...props}
      finishOutcome={outcomes.get(messageId)?.[reasoningIndex]}
    />
  );
}
